// ============================================================================
// JARVISTRAVEL - POST-SIGN-IN RETURN PATH
// Where to send the user after they sign in, when a deep link (e.g. an emailed
// collaborator claim) required auth first. sessionStorage on purpose: the stash
// is per-tab and gone when the tab closes, so a stale path can never redirect a
// later, unrelated sign-in. JAR-285.
// ============================================================================

const RETURN_TO_KEY = 'jarvis_return_to';

// Within a tab, a path waits for the sign-in, sign-up, MFA enrolment or
// checkout it was saved for, not for the rest of the tab's life. Two hours
// covers a slow sign-up with its email code and an authenticator to set up.
// After that it was abandoned, and resuming it would take over an unrelated
// sign-in.
const RETURN_TO_TTL_MS = 2 * 60 * 60 * 1000;

/** Stash an in-app path (with query) to resume after sign-in. */
export function saveReturnTo(path: string): void {
  try {
    sessionStorage.setItem(RETURN_TO_KEY, JSON.stringify({ path, savedAt: Date.now() }));
  } catch {
    /* storage unavailable — sign-in just lands on the default screen */
  }
}

/**
 * Drop the stash if it is `path`: the page at that path has settled, so there
 * is nothing left to return to. A stash for any other path stays.
 */
export function forgetReturnTo(path: string): void {
  try {
    const raw = sessionStorage.getItem(RETURN_TO_KEY);
    if (raw && (JSON.parse(raw) as { path?: unknown }).path === path) sessionStorage.removeItem(RETURN_TO_KEY);
  } catch {
    /* storage unavailable, or a stash it can't read: nothing to drop */
  }
}

/**
 * Read the stashed path without clearing it, applying the same validation as
 * {@link consumeReturnTo} (same-app absolute path, within the TTL). Lets a caller
 * observe whether a return path is still pending without consuming it — e.g. a
 * test waiting for the page that settles the invitation to drop its own stash.
 */
export function peekReturnTo(): string | null {
  try {
    const raw = sessionStorage.getItem(RETURN_TO_KEY);
    if (!raw) return null;
    const { path, savedAt } = JSON.parse(raw) as { path?: unknown; savedAt?: unknown };
    if (typeof path !== 'string' || typeof savedAt !== 'number' || Date.now() - savedAt > RETURN_TO_TTL_MS) return null;
    return path.startsWith('/') && !path.startsWith('//') ? path : null;
  } catch {
    return null;
  }
}

/**
 * Read and clear the stashed path. Only same-app absolute paths saved within
 * the last two hours come back — protocol-relative ("//host") and absolute URLs
 * are dropped so the stash can never become an open redirect.
 */
export function consumeReturnTo(): string | null {
  try {
    const raw = sessionStorage.getItem(RETURN_TO_KEY);
    sessionStorage.removeItem(RETURN_TO_KEY);
    if (!raw) return null;
    const { path, savedAt } = JSON.parse(raw) as { path?: unknown; savedAt?: unknown };
    if (typeof path !== 'string' || typeof savedAt !== 'number' || Date.now() - savedAt > RETURN_TO_TTL_MS) return null;
    return path.startsWith('/') && !path.startsWith('//') ? path : null;
  } catch {
    return null;
  }
}
